import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SupportAssistant from '../components/SupportAssistant';
import { 
  Zap, Target, Shield, Heart, ArrowRight, 
  Users, Brain, Clock, CheckCircle
} from 'lucide-react';

const QuiSommesNousPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 py-4 px-4 sm:px-6 lg:px-8 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800" style={{fontFamily: "'Outfit', sans-serif"}}>SmartFacture AI</span>
          </Link>
          <Link to="/register">
            <Button className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              Essai gratuit
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-16 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6 leading-tight" style={{fontFamily: "'Outfit', sans-serif"}}>
            L'outsider qui simplifie<br />
            <span className="text-blue-600">la facturation 2026</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            SmartFacture AI est né d'un constat simple : la réforme e-facturation 2026 
            va impacter 4 millions d'entreprises en France. La plupart ne sont pas prêtes, 
            et les solutions existantes sont trop chères ou trop compliquées.
          </p>
        </div>
      </section>

      {/* Our Mission */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{fontFamily: "'Outfit', sans-serif"}}>
                Notre mission
              </h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                Rendre la conformité e-facturation <strong>accessible à tous</strong>, pas seulement 
                aux grandes entreprises qui peuvent se payer des ERP à 500€/mois.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Avec SmartFacture, vous scannez vos documents, l'IA s'occupe du reste. 
                <strong> Zéro charge mentale, zéro erreur, conformité automatique.</strong>
              </p>
              <div className="space-y-3">
                {[
                  "Conçu pour les TPE/PME et auto-entrepreneurs",
                  "IA française qui comprend vos factures",
                  "Conforme Factur-X dès le premier jour",
                  "Prix juste, sans engagement"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Target, label: "Notre objectif", value: "Rendre la conformité 2026 simple", color: "blue" },
                { icon: Users, label: "Notre cible", value: "TPE, PME, auto-entrepreneurs", color: "violet" },
                { icon: Brain, label: "Notre arme", value: "L'intelligence artificielle", color: "emerald" },
                { icon: Heart, label: "Notre promesse", value: "Pas cher, efficace, sans stress", color: "amber" }
              ].map((item, idx) => (
                <Card key={idx} className="border-slate-200">
                  <CardContent className="p-5">
                    <item.icon className={`h-8 w-8 text-${item.color}-500 mb-3`} />
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{item.label}</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{item.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why we're different */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto py-16">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10" style={{fontFamily: "'Outfit', sans-serif"}}>
            Pourquoi SmartFacture AI ?
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Simple et rapide",
                desc: "Pas de formation, pas de configuration complexe. Inscrivez-vous, scannez, facturez. En 2 minutes c'est prêt."
              },
              {
                icon: Shield,
                title: "100% conforme 2026",
                desc: "Format Factur-X natif, toutes les mentions obligatoires vérifiées automatiquement, score de conformité en temps réel."
              },
              {
                icon: Clock,
                title: "Gain de temps massif",
                desc: "Notre IA extrait automatiquement les données de vos documents. Fini la saisie manuelle, fini les erreurs."
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-5">
                  <item.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-800 mb-3">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto py-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {[
              { value: "14€", label: "par mois (Starter)" },
              { value: "2 min", label: "pour démarrer" },
              { value: "100%", label: "conforme Factur-X" },
              { value: "24/7", label: "assistant disponible" }
            ].map((item, idx) => (
              <div key={idx}>
                <p className="text-3xl sm:text-4xl font-bold text-blue-600" style={{fontFamily: "'Outfit', sans-serif"}}>{item.value}</p>
                <p className="text-sm text-slate-500 mt-2">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <Card className="bg-gradient-to-r from-sky-50 to-blue-100 border border-sky-200 shadow-xl">
            <CardContent className="p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4" style={{fontFamily: "'Playfair Display', serif"}}>
                Prêt à simplifier votre facturation ?
              </h2>
              <p className="text-slate-600 mb-8 text-lg">
                Rejoignez les entreprises qui ont choisi la simplicité.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-10 rounded-full text-lg font-semibold shadow-lg shadow-sky-200" data-testid="about-cta">
                  Essai gratuit 7 jours
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer link */}
      <div className="text-center pb-10">
        <Link to="/" className="text-blue-600 hover:underline text-sm">Retour à l'accueil</Link>
        <span className="mx-3 text-slate-300">|</span>
        <Link to="/comparaison" className="text-blue-600 hover:underline text-sm">Comparer avec la concurrence</Link>
      </div>

      <SupportAssistant />
    </div>
  );
};

export default QuiSommesNousPage;
